import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CustomInstructionsFormComponent,
  CustomInstructionValue,
} from '@components/forms/custom-instructions-form/custom-instructions-form.component';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

const GUEST_OPTIONS = [
  { label: 'Only Narrator', value: 0 },
  { label: '1 Guest', value: 1 },
];

const createSectionOptions = () =>
  Array.from({ length: 9 }, (_, i) => ({ label: `${i + 4}`, value: i + 4 }));

export interface PodcastSettingsData {
  customInstruction: CustomInstructionValue;
  guests: number;
  sections: number;
}

export interface PodcastSettingsPayload {
  customInstruction: CustomInstructionValue;
  guests: number;
  sections: number;
}

type FormGroupType = FormGroup<{
  guests: FormControl<number | null>;
  sections: FormControl<number | null>;
}>;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    SelectButtonModule,
    SelectModule,
    ButtonModule,
    TextareaModule,
    FloatLabelModule,
    ReactiveFormsModule,
    FormsModule,
    ToggleSwitchModule,
    CustomInstructionsFormComponent,
  ],
  templateUrl: './podcast-settings-dialog-content.component.html',
  styleUrl: './podcast-settings-dialog-content.component.css',
})
export class PodcastSettingsComponent {
  form!: FormGroupType;
  guestOptions = GUEST_OPTIONS;
  sectionOptions = createSectionOptions();

  customInstruction: CustomInstructionValue;

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig<PodcastSettingsData>,
  ) {
    this.createForm();
  }

  private createForm() {
    const { data } = this.dialogConfig;
    const { customInstruction, guests, sections } = data;

    this.customInstruction = customInstruction;
    this.form = this.formBuilder.group({
      guests: this.formBuilder.control(guests, [Validators.required]),
      sections: this.formBuilder.control(sections, [Validators.required]),
    });
  }

  onCustomInstructionChange(value: CustomInstructionValue): void {
    this.customInstruction = value;
  }

  public handleCancelClick() {
    this.dialogRef.close();
  }

  public handleSaveClick() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: PodcastSettingsPayload = {
      customInstruction: this.customInstruction,
      ...this.form.getRawValue(),
    };

    this.dialogRef.close(payload);
  }
}
